/**
 * Ticket-Detail (Spec 7.2): Verlauf links, Eigenschaften und Kundenkontext
 * rechts, Antwort-Editor darunter.
 *
 * Mit Phase 5 ist das Kundenkontext-Panel vollstaendig (Spec 7.2): Kontakt,
 * Quell-Metadaten, SkillLock-Tasks, CSAT-Historie und aehnliche Tickets. Dazu
 * kommen Presence (Spec 7.1), Makros (8.2) und der Merge (7.2).
 *
 * Die Presence-Absicht haengt an genau zwei Zustaenden: **geoeffnet** (immer,
 * solange die Seite steht) und **bearbeitet** (Antwort-Editor angefasst). Mehr
 * Feinheit braucht ein Support-Team nicht, und jedes weitere Signal waere ein
 * weiterer Grund fuer eine falsche Warnung.
 */
import {
  isOverdue,
  roleHasPermission,
  type AiDraft,
  type AiSuggestion,
  type CsatResponse,
  type Macro,
  type PresenceEntry,
  type Queue,
  type SimilarTicket,
  type SkilllockTaskLink,
  type Source,
  type Tag,
  type Team,
  type TenantMemberDetail,
  type TicketDetail,
  type TicketPriority,
  type TicketStatus,
  type TiptapDoc,
} from '@tickethub/shared';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { AiDraftPanel } from '@/components/AiDraftPanel';
import { AiTriageBanner } from '@/components/AiTriageBanner';
import { CsatPanel } from '@/components/CsatPanel';
import { KbCuratePanel } from '@/components/KbCuratePanel';
import { MacroBar } from '@/components/MacroBar';
import { MergePanel } from '@/components/MergePanel';
import { MessageThread } from '@/components/MessageThread';
import { PresenceBanner } from '@/components/PresenceBanner';
import { ReplyEditor } from '@/components/ReplyEditor';
import { SimilarTickets } from '@/components/SimilarTickets';
import { SkilllockPanel } from '@/components/SkilllockPanel';
import { TicketMetadata } from '@/components/TicketMetadata';
import { TicketProperties } from '@/components/TicketProperties';
import { WikiSearch } from '@/components/WikiSearch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toastError } from '@/lib/errors';
import { useTicketEvents } from '@/lib/realtime';
import { useSession } from '@/lib/session';
import {
  aiApi,
  attachmentsApi,
  csatApi,
  macrosApi,
  membersApi,
  queuesApi,
  skilllockApi,
  snippetsApi,
  sourcesApi,
  tagsApi,
  teamsApi,
  ticketsApi,
} from '@/lib/tickets';
import type { Snippet } from '@tickethub/shared';

export function TicketDetailPage() {
  const { t, i18n } = useTranslation();
  const { ticketId = '' } = useParams();
  const { activeMembership } = useSession();
  const tenantId = activeMembership?.tenant_id ?? null;

  const canWrite = activeMembership
    ? roleHasPermission(activeMembership.role, 'ticket.write')
    : true;
  /** `ai_enabled = false` blendet die komplette KI-Oberflaeche aus (Spec 3.2). */
  const aiEnabled = activeMembership?.ai_enabled === true;

  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [members, setMembers] = useState<TenantMemberDetail[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [queues, setQueues] = useState<Queue[]>([]);
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [macros, setMacros] = useState<Macro[]>([]);
  const [extensions, setExtensions] = useState<string[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [notFound, setNotFound] = useState(false);

  // ── Phase 5: Presence, Workflow, Integrationen ───────────────────────────
  const [presence, setPresence] = useState<PresenceEntry[]>([]);
  const [editing, setEditing] = useState(false);
  const [csat, setCsat] = useState<CsatResponse[]>([]);
  const [tasks, setTasks] = useState<SkilllockTaskLink[]>([]);
  const [skilllockEnabled, setSkilllockEnabled] = useState(false);
  /** Snippet, das ein Makro in den Editor legen soll (Spec 8.2). */
  const [macroSnippet, setMacroSnippet] = useState<{ id: string; body_json: TiptapDoc } | null>(null);

  // ── KI (Spec 9) ──────────────────────────────────────────────────────────
  const [draft, setDraft] = useState<AiDraft | null>(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const [takenDraft, setTakenDraft] = useState<{ id: string; body_json: TiptapDoc } | null>(null);
  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([]);
  const [similar, setSimilar] = useState<SimilarTicket[]>([]);
  const [similarLoading, setSimilarLoading] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId || !ticketId) return;
    try {
      setTicket(await ticketsApi.get(tenantId, ticketId));
    } catch (error) {
      setNotFound(true);
      toastError(error, t);
    }
  }, [tenantId, ticketId, t]);

  useEffect(() => {
    // `load`/`reload` setzen State erst *nach* dem Netzwerk-Await — es entsteht
    // keine kaskadierende Render-Schleife. Die Regel kann das nicht unterscheiden.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void load();
  }, [load]);

  useEffect(() => {
    if (!tenantId) return;
    void (async () => {
      try {
        const [tagResult, snippetResult, typeResult, sourceResult, queueResult, teamResult, macroResult] =
          await Promise.all([
            tagsApi.list(tenantId),
            snippetsApi.list(tenantId),
            attachmentsApi.allowedTypes(tenantId),
            // Fuer `metadata_display` der Quelle (Spec 5.2) — die Liste ist
            // klein und liegt danach fuer jedes Ticket dieses Mandanten bereit.
            sourcesApi.list(tenantId),
            // Namen fuer den Queue-Vorschlag der Triage (Spec 9.3).
            queuesApi.list(tenantId),
            teamsApi.list(tenantId),
            macrosApi.list(tenantId),
          ]);
        setTags(tagResult.items);
        setSnippets(snippetResult.items);
        setExtensions(typeResult.items.map((type) => type.extension));
        setSources(sourceResult.items);
        setQueues(queueResult.items);
        setTeams(teamResult.items);
        setMacros(macroResult.items);
      } catch (error) {
        toastError(error, t);
      }

      // SkillLock-Einstellungen sieht nur `tenant.manage`; ein Agent arbeitet
      // dann ohne Wiki-Suche und ohne „Task anlegen" weiter.
      try {
        setSkilllockEnabled((await skilllockApi.getSettings(tenantId)).enabled);
      } catch {
        setSkilllockEnabled(false);
      }

      // Die Mitgliederliste darf nur `member.manage` — Agents bekommen ein
      // 403 und arbeiten dann ohne Zuweisungs-Auswahl weiter.
      try {
        setMembers((await membersApi.list(tenantId)).items);
      } catch {
        setMembers([]);
      }
    })();
  }, [tenantId, t]);

  /** CSAT, SkillLock-Tasks und Presence — der Kundenkontext aus Spec 7.2. */
  const loadContext = useCallback(async () => {
    if (!tenantId || !ticketId) return;
    try {
      setCsat((await csatApi.forTicket(tenantId, ticketId)).items);
    } catch {
      setCsat([]);
    }
    try {
      setTasks((await skilllockApi.tasks(tenantId, ticketId)).items);
    } catch {
      setTasks([]);
    }
    try {
      setPresence((await ticketsApi.presence(tenantId, ticketId)).items);
    } catch {
      setPresence([]);
    }
  }, [tenantId, ticketId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void loadContext();
  }, [loadContext]);

  /** Entwuerfe, Vorschlaege und aehnliche Tickets — alle drei KI-Panels. */
  const loadAi = useCallback(async () => {
    if (!tenantId || !ticketId || !aiEnabled) return;

    try {
      const [draftResult, suggestionResult] = await Promise.all([
        canWrite ? aiApi.listDrafts(tenantId, ticketId) : Promise.resolve({ items: [] }),
        aiApi.listSuggestions(tenantId, ticketId),
      ]);
      // Der neueste Entwurf, der nicht verworfen wurde — mehr zeigt das Panel nicht.
      setDraft(draftResult.items.find((entry) => entry.status !== 'discarded') ?? null);
      setSuggestions(suggestionResult.items);
    } catch {
      // KI abgeschaltet oder nicht eingerichtet: die Panels bleiben leer.
      setDraft(null);
      setSuggestions([]);
    }

    setSimilarLoading(true);
    try {
      setSimilar((await aiApi.similar(tenantId, ticketId)).items);
    } catch {
      setSimilar([]);
    } finally {
      setSimilarLoading(false);
    }
  }, [tenantId, ticketId, aiEnabled, canWrite]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void loadAi();
  }, [loadAi]);

  const presenceIntent = useMemo(
    () => (ticketId ? { ticketId, editing, typing: false } : null),
    [ticketId, editing],
  );

  // Live: nur Ereignisse zu genau diesem Ticket laden das Detail neu.
  useTicketEvents(
    tenantId,
    (event) => {
      if (event.ticket_id !== ticketId) return;
      if (event.type === 'presence.updated') {
        setPresence(event.presence);
        return;
      }
      if (event.type === 'ai.draft_updated') {
        // Der Job ist durch — genau darauf wartet der Agent nach dem Klick.
        setDraftBusy(false);
        void loadAi();
        return;
      }
      void load();
      void loadContext();
    },
    presenceIntent,
  );

  async function requestDraft() {
    if (!tenantId) return;
    setDraftBusy(true);
    try {
      setDraft(await aiApi.requestDraft(tenantId, ticketId));
    } catch (error) {
      setDraftBusy(false);
      toastError(error, t);
    }
  }

  /**
   * „Übernehmen": der Entwurf geht in den Antwort-Editor und bekommt den
   * Status `edited`. **Gesendet wird er dort** — mit demselben Klick wie eine
   * selbst getippte Antwort (Architektur-Invariante 3).
   */
  async function takeDraft(entry: AiDraft) {
    if (!tenantId) return;
    setTakenDraft({ id: entry.id, body_json: entry.draft_json });
    try {
      await aiApi.decideDraft(tenantId, entry.id, { status: 'edited' });
      await loadAi();
    } catch (error) {
      toastError(error, t);
    }
  }

  async function discardDraft(entry: AiDraft) {
    if (!tenantId) return;
    try {
      await aiApi.decideDraft(tenantId, entry.id, { status: 'discarded' });
      setTakenDraft(null);
      await loadAi();
    } catch (error) {
      toastError(error, t);
    }
  }

  async function decideSuggestion(suggestion: AiSuggestion, accept: boolean) {
    if (!tenantId) return;
    try {
      if (accept) {
        setTicket(await aiApi.acceptSuggestion(tenantId, suggestion.id));
      } else {
        await aiApi.rejectSuggestion(tenantId, suggestion.id);
      }
      await loadAi();
    } catch (error) {
      toastError(error, t);
      await load();
    }
  }

  /**
   * Makro ausfuehren (Spec 8.2). Der Server erledigt alles ausser dem
   * Textbaustein — den legt der Browser in den Editor, weil ein Server keine
   * Nachricht an den Kunden schreiben darf.
   */
  async function runMacro(macro: Macro) {
    if (!tenantId) return null;
    try {
      const result = await macrosApi.run(tenantId, macro.id, ticketId);
      const snippetId = result.insert_snippet_ids[0];
      const snippet = snippets.find((entry) => entry.id === snippetId);
      if (snippet) setMacroSnippet({ id: snippet.id, body_json: snippet.body_json });
      await load();
      await loadContext();
      return result;
    } catch (error) {
      toastError(error, t);
      return null;
    }
  }

  async function mutate(action: () => Promise<TicketDetail>) {
    try {
      setTicket(await action());
    } catch (error) {
      toastError(error, t);
      void load();
    }
  }

  async function merge(request: { targetTicketId: string; mergeContacts: boolean }) {
    if (!tenantId) return;
    try {
      await ticketsApi.merge(tenantId, ticketId, {
        target_ticket_id: request.targetTicketId,
        merge_contacts: request.mergeContacts,
      });
      await load();
      await loadAi();
    } catch (error) {
      toastError(error, t);
    }
  }

  async function requestCsat() {
    if (!tenantId) return;
    try {
      await csatApi.request(tenantId, ticketId);
      await loadContext();
    } catch (error) {
      toastError(error, t);
    }
  }

  async function createTask(title: string) {
    if (!tenantId) return;
    try {
      await skilllockApi.createTask(tenantId, ticketId, title === '' ? {} : { title });
      // 202: der Job laeuft; das Ergebnis kommt per `ticket.updated`.
    } catch (error) {
      toastError(error, t);
    }
  }

  if (!activeMembership) {
    return (
      <Alert>
        <AlertDescription>{t('tenant.empty')}</AlertDescription>
      </Alert>
    );
  }

  if (notFound) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{t('ticket.not_found')}</AlertDescription>
      </Alert>
    );
  }

  if (!ticket || !tenantId) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/tickets">
            <ArrowLeft aria-hidden="true" />
            {t('ticket.back_to_list')}
          </Link>
        </Button>
        <span className="font-mono text-xs text-muted-foreground">{ticket.number}</span>
        <h1 className="text-2xl font-semibold">{ticket.subject}</h1>
        <Badge variant="secondary">{t(`ticket.status_${ticket.status}`)}</Badge>
        {isOverdue(ticket) ? (
          <Badge variant="destructive">
            <AlertTriangle aria-hidden="true" className="size-3" />
            {t('ticket.overdue')}
          </Badge>
        ) : null}
      </div>

      {/* Kollisionserkennung (Spec 7.1) — wer sonst noch am Ticket sitzt. */}
      <PresenceBanner entries={presence} ownMemberId={activeMembership.member_id} />

      {aiEnabled ? (
        <AiTriageBanner
          suggestions={suggestions}
          tags={tags}
          queues={queues}
          disabled={!canWrite}
          onAccept={(suggestion) => void decideSuggestion(suggestion, true)}
          onReject={(suggestion) => void decideSuggestion(suggestion, false)}
        />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-4">
          <MessageThread messages={ticket.messages} locale={i18n.language} />

          {/* Makros (Spec 8.2): ein Klick, alle Aktionen der Reihe nach. */}
          <MacroBar macros={macros} disabled={!canWrite} onRun={runMacro} />

          {aiEnabled && canWrite ? (
            <AiDraftPanel
              draft={draft}
              busy={draftBusy}
              disabled={!canWrite}
              onRequest={() => void requestDraft()}
              onTake={(entry) => void takeDraft(entry)}
              onDiscard={(entry) => void discardDraft(entry)}
            />
          ) : null}

          {canWrite ? (
            <div
              // „Editor angefasst" ist das weiche Lock aus Spec 7.1 — es
              // beginnt beim ersten Fokus, nicht beim Oeffnen der Seite.
              onFocusCapture={() => setEditing(true)}
            >
              <ReplyEditor
                snippets={snippets}
                acceptedExtensions={extensions}
                aiDraft={takenDraft ?? macroSnippet}
                onUpload={(file) => attachmentsApi.upload(tenantId, file)}
                onSubmit={async (input) => {
                  await mutate(() => ticketsApi.addMessage(tenantId, ticket.id, input));
                  setTakenDraft(null);
                  setMacroSnippet(null);
                  setEditing(false);
                  await loadAi();
                  await loadContext();
                }}
              />
            </div>
          ) : (
            <Alert>
              <AlertDescription>{t('ticket.read_only')}</AlertDescription>
            </Alert>
          )}
        </div>

        <aside className="space-y-4">
          <TicketProperties
            ticket={ticket}
            members={members}
            teams={teams}
            tags={tags}
            readOnly={!canWrite}
            onStatusChange={(status: TicketStatus) =>
              void mutate(() => ticketsApi.setStatus(tenantId, ticket.id, status))
            }
            onPriorityChange={(priority: TicketPriority) =>
              void mutate(() => ticketsApi.update(tenantId, ticket.id, { priority }))
            }
            onAssigneeChange={(assigneeMemberId) =>
              void mutate(() =>
                ticketsApi.update(tenantId, ticket.id, {
                  assignee_member_id: assigneeMemberId,
                }),
              )
            }
            onTeamChange={(teamId) =>
              void mutate(() => ticketsApi.update(tenantId, ticket.id, { team_id: teamId }))
            }
            onDueAtChange={(dueAt) =>
              void mutate(() => ticketsApi.update(tenantId, ticket.id, { due_at: dueAt }))
            }
            onTagsChange={(tagIds) =>
              void mutate(() => ticketsApi.setTags(tenantId, ticket.id, tagIds))
            }
          />

          <section className="space-y-2 rounded-lg border p-4">
            <h2 className="text-sm font-medium">{t('contact.title')}</h2>
            <p className="text-sm">{ticket.contact.display_name}</p>
            {ticket.contact.primary_email ? (
              <p className="text-sm text-muted-foreground">{ticket.contact.primary_email}</p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {t('ticket.queue')}: {ticket.queue_name}
              {ticket.source_name ? ` · ${ticket.source_name}` : ''}
            </p>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/contacts/${ticket.contact_id}`}>{t('contact.open')}</Link>
            </Button>
          </section>

          {/* Merge + Duplikat-Hinweis (Spec 7.2 / 9.3). */}
          {canWrite ? (
            <MergePanel
              ticket={ticket}
              similar={similar}
              disabled={!canWrite}
              onMerge={merge}
            />
          ) : null}

          {/* SkillLock-Tasks (Spec 10) im Kundenkontext-Panel. */}
          <SkilllockPanel
            tasks={tasks}
            enabled={skilllockEnabled || tasks.length > 0}
            disabled={!canWrite}
            onCreate={createTask}
          />

          {/* CSAT-Historie (Spec 8.6). */}
          <CsatPanel
            items={csat}
            enabled={activeMembership.csat_enabled || csat.length > 0}
            disabled={!canWrite}
            onRequest={requestCsat}
            locale={i18n.language}
          />

          {/* Wiki-Live-Suche (Spec 9.4) — dieselbe Quelle wie der KI-Entwurf. */}
          {canWrite ? (
            <WikiSearch
              enabled={skilllockEnabled}
              initialQuery={ticket.subject}
              onSearch={(query) => skilllockApi.searchWiki(tenantId, query)}
            />
          ) : null}

          {/* „So wurde es geloest" (Spec 9.5). */}
          {aiEnabled ? <SimilarTickets items={similar} loading={similarLoading} /> : null}

          {/* „KB-wuerdig" — erst wenn das Ticket geloest ist (Spec 9.4). */}
          {aiEnabled && canWrite && (ticket.status === 'resolved' || ticket.status === 'closed') ? (
            <KbCuratePanel
              disabled={!canWrite}
              onDraft={() => aiApi.kbDraftFromTicket(tenantId, ticket.id)}
              onSave={async (input) => {
                await aiApi.createKb(tenantId, {
                  title: input.title,
                  body_text: input.body_text,
                  origin: 'curated_ticket',
                  source_ticket_id: input.source_ticket_id,
                  active: true,
                });
              }}
            />
          ) : null}

          {/* Quell-Metadaten (Spec 5.2) — Anzeige nach `metadata_display`
              der Quelle, unbekannte Schluessel generisch. */}
          <TicketMetadata
            metadata={ticket.metadata}
            display={
              sources.find((source) => source.id === ticket.source_id)?.metadata_display ?? null
            }
          />
        </aside>
      </div>
    </div>
  );
}
